// Skill taxonomy types for challenge tagging and parent reporting
import { z } from "zod";
// ==================== Math Skills ====================
export const MathSkillKeys = [
    "counting_1_5",
    "counting_6_10",
    "counting_11_20",
    "number_recognition",
    "addition_sums_5",
    "addition_sums_10",
    "subtraction_from_5",
    "subtraction_from_10",
    "comparison_more_less",
    "comparison_equal",
];
// ==================== Spelling Skills ====================
export const SpellingSkillKeys = [
    "letter_recognition_uppercase",
    "letter_recognition_lowercase",
    "letter_matching",
    "beginning_sounds_consonants",
    "beginning_sounds_vowels",
    "ending_sounds",
    "vowel_sounds_short",
    "vowel_sounds_long",
    "cvc_words",
    "sight_words",
];
// ==================== Pattern Skills ====================
export const PatternSkillKeys = [
    "pattern_ab",
    "pattern_abb",
    "pattern_abc",
    "pattern_colors",
    "pattern_shapes",
    "pattern_sizes",
    "pattern_completion",
    "pattern_creation",
    "pattern_mixed",
];
// ==================== Typing Skills ====================
export const TypingSkillKeys = [
    "keyboard_familiarity",
    "key_finding_letters",
    "key_finding_numbers",
    "home_row_keys",
    "letter_sequences",
    "simple_words",
    "cvc_word_typing",
    "short_phrases",
];
// ==================== Programming Skills ====================
export const ProgrammingSkillKeys = [
    "directional_commands",
    "single_command_execution",
    "sequential_commands",
    "maze_simple",
    "maze_complex",
    "item_collection",
    "path_optimization",
    "loops_repeat",
    "debugging_simple",
];
// ==================== Combined Skill Types ====================
/** All valid skill keys across all disciplines */
export const AllSkillKeys = [
    ...MathSkillKeys,
    ...SpellingSkillKeys,
    ...PatternSkillKeys,
    ...TypingSkillKeys,
    ...ProgrammingSkillKeys,
];
/** Mapping of discipline slugs to their skill key arrays */
export const SkillKeysByDiscipline = {
    math: MathSkillKeys,
    spelling: SpellingSkillKeys,
    patterns: PatternSkillKeys,
    typing: TypingSkillKeys,
    programming: ProgrammingSkillKeys,
};
// ==================== Zod Schemas ====================
/** Zod schema for math skill keys */
export const MathSkillKeySchema = z.enum(MathSkillKeys);
/** Zod schema for spelling skill keys */
export const SpellingSkillKeySchema = z.enum(SpellingSkillKeys);
/** Zod schema for pattern skill keys */
export const PatternSkillKeySchema = z.enum(PatternSkillKeys);
/** Zod schema for typing skill keys */
export const TypingSkillKeySchema = z.enum(TypingSkillKeys);
/** Zod schema for programming skill keys */
export const ProgrammingSkillKeySchema = z.enum(ProgrammingSkillKeys);
/** Zod schema for any skill key */
export const SkillKeySchema = z.enum(AllSkillKeys);
/** Zod schema for an array of skill keys (skill tags) */
export const SkillTagsSchema = z.array(SkillKeySchema);
/** Zod schema for discipline slugs */
export const DisciplineSlugSchema = z.enum([
    "math",
    "spelling",
    "patterns",
    "typing",
    "programming",
]);
/** ISO datetime string schema */
const ISODateTimeSchema = z.string().refine((val) => {
    const date = new Date(val);
    return !isNaN(date.getTime());
}, { message: "Invalid ISO datetime string" });
/** Zod schema for the Skill interface */
export const SkillSchema = z.object({
    id: z.string().uuid(),
    disciplineId: z.string().uuid(),
    skillKey: SkillKeySchema,
    displayName: z.string().min(1),
    description: z.string().optional(),
    parentSkillId: z.string().uuid().nullable(),
    difficultyOrder: z.number().int().positive(),
    isActive: z.boolean(),
    createdAt: ISODateTimeSchema,
});
// ==================== Helper Functions ====================
/**
 * Get skill keys for a specific discipline
 */
export function getSkillKeysForDiscipline(discipline) {
    return SkillKeysByDiscipline[discipline];
}
/**
 * Check if a skill key belongs to a specific discipline
 */
export function isSkillKeyForDiscipline(skillKey, discipline) {
    const disciplineKeys = SkillKeysByDiscipline[discipline];
    return disciplineKeys.includes(skillKey);
}
/**
 * Get the discipline for a given skill key
 */
export function getDisciplineForSkillKey(skillKey) {
    for (const [discipline, keys] of Object.entries(SkillKeysByDiscipline)) {
        if (keys.includes(skillKey)) {
            return discipline;
        }
    }
    return undefined;
}
/**
 * Validate that skill tags are valid for a discipline
 */
export function validateSkillTagsForDiscipline(skillTags, discipline) {
    const validKeys = SkillKeysByDiscipline[discipline];
    const invalidTags = skillTags.filter((tag) => !validKeys.includes(tag));
    return {
        valid: invalidTags.length === 0,
        invalidTags,
    };
}
//# sourceMappingURL=skills.js.map